import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  useTheme,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';

const BudgetTab = ({ data, loading, isSnapshot = false, snapshotMonth = null, enhancedMetrics = null }) => {
  const theme = useTheme();
  
  // Debug data structure - Compare current vs snapshot
  console.log('🐛 BudgetTab Debug:', {
    isSnapshot,
    snapshotMonth,
    dataType: typeof data,
    dataLength: data?.length,
    firstBrand: data?.[0],
    firstCampaign: data?.[0]?.campaigns?.[0],
    firstMediaType: data?.[0]?.campaigns?.[0]?.media_types?.[0],
    monthlyBreakdown: data?.[0]?.campaigns?.[0]?.media_types?.[0]?.monthly_breakdown,
    // Key differences to check
    brandStructure: {
      brand_name: data?.[0]?.brand_name,
      campaigns_count: data?.[0]?.campaigns?.length,
      campaigns_exist: !!data?.[0]?.campaigns,
      first_campaign_media_types: data?.[0]?.campaigns?.[0]?.media_types?.length || 0
    }
  });
  
  // Brand filter state
  const [selectedBrand, setSelectedBrand] = useState('All');

  // Determine the "current" date context based on snapshot or live mode
  const getContextDate = useMemo(() => {
    if (isSnapshot && snapshotMonth) {
      // Parse snapshot month (format: "2025-12")
      const [year, month] = snapshotMonth.split('-').map(Number);
      console.log(`📅 BudgetTab using snapshot month: ${snapshotMonth} as context date`);
      return new Date(year, month - 1, 15); // Mid of snapshot month
    }
    console.log(`📅 BudgetTab using live current date as context`);
    return new Date(); // Live current date
  }, [isSnapshot, snapshotMonth]);

  // Generate timeline for all 12 months
  const timelineMonths = useMemo(() => {
    const months = [];
    const contextDate = getContextDate;
    const currentYear = contextDate.getFullYear();
    
    // Current month highlight in UI uses n-2 for consistency (both live and snapshot)
    const actualCurrentMonth = contextDate.getMonth(); // 0-based
    const displayCurrentMonth = Math.max(0, actualCurrentMonth - 2);
    
    console.log(`📅 BudgetTab timeline context: ${isSnapshot ? 'Snapshot' : 'Live'} mode, display current month (n-2): ${displayCurrentMonth + 1}, actual context month: ${actualCurrentMonth + 1} (${currentYear})`);
    
    // Show all 12 months (January to December)
    for (let i = 0; i < 12; i++) {
      const month = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      months.push({
        year: currentYear,
        month: i + 1, // 1-based
        name: month.toLocaleString('en-US', { month: 'short' }),
        fullName: month.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        startDate: month,
        endDate: monthEnd,
        isCurrentMonth: i === displayCurrentMonth, // Current based on context
        isFutureColumn: false
      });
    }
    
    return months;
  }, [getContextDate, isSnapshot]);

  // Calculate total planning cost for each month from all campaigns
  const calculateMonthlyPlanningTotals = useMemo(() => {
    if (!data || !Array.isArray(data)) return {};

    const monthlyPlanningTotals = {};
    const contextDate = getContextDate;
    const currentYear = contextDate.getFullYear();
    
    // Initialize all months with 0
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${currentYear}-${month.toString().padStart(2, '0')}`;
      monthlyPlanningTotals[monthKey] = 0;
    }

    // Sum planning cost for each month from all campaigns
    data.forEach(brand => {
      if (!brand.campaigns || !Array.isArray(brand.campaigns)) return;

      brand.campaigns.forEach(campaign => {
        const mediaTypesData = campaign.media_types || [];
        
        mediaTypesData.forEach(mediaType => {
          if (mediaType.monthly_breakdown && Array.isArray(mediaType.monthly_breakdown)) {
            mediaType.monthly_breakdown.forEach(monthData => {
              // Check if this is a future summary that needs to be distributed
              if (monthData.is_future_summary && campaign.start_date && campaign.end_date) {
                // Distribute the planning cost across the campaign duration
                const startDate = new Date(campaign.start_date);
                const endDate = new Date(campaign.end_date);
                const planningValue = parseFloat(monthData.planning_cost) || 0;
                
                // Calculate number of months in campaign duration
                const startMonth = startDate.getMonth() + 1; // 1-based
                const endMonth = endDate.getMonth() + 1; // 1-based
                const startYear = startDate.getFullYear();
                const endYear = endDate.getFullYear();
                
                // For simplicity, assume same year campaigns
                if (startYear === endYear) {
                  const monthsInCampaign = endMonth - startMonth + 1;
                  const planningPerMonth = planningValue / monthsInCampaign;
                  
                  // Distribute across months
                  for (let month = startMonth; month <= endMonth; month++) {
                    const monthKey = `${startYear}-${month.toString().padStart(2, '0')}`;
                    if (monthlyPlanningTotals.hasOwnProperty(monthKey)) {
                      monthlyPlanningTotals[monthKey] += planningPerMonth;
                      console.log(`📅 Distributed ${planningPerMonth.toLocaleString()} to ${monthKey} for campaign ${campaign.campaign_name}`);
                    }
                  }
                }
              } else {
                // Regular monthly data
                const monthKey = monthData.month; // Format: "2025-05"
                if (monthlyPlanningTotals.hasOwnProperty(monthKey)) {
                  // Sum planning cost for this month
                  const planningValue = parseFloat(monthData.planning_cost) || 0;
                  monthlyPlanningTotals[monthKey] += planningValue;
                }
              }
            });
          }
        });
      });
    });

    console.log('📊 Monthly planning totals:', monthlyPlanningTotals);
    return monthlyPlanningTotals;
  }, [data, getContextDate]);

  // Process data into flat campaign list with simplified timeline
  const campaignData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    const campaigns = [];
    const contextDate = getContextDate;
    const actualCurrentMonth = contextDate.getMonth() + 1; // 1-based 
    const currentYear = contextDate.getFullYear();
    
    // Business Rule update: Use n-2 for snapshot; live uses actual month
    // For snapshot month 6 (June), YTD should be up to month 4 (April)
    const calculationCurrentMonth = isSnapshot ? Math.max(1, actualCurrentMonth - 2) : actualCurrentMonth; 
    const previousMonth = calculationCurrentMonth - 1; 

    console.log(`📊 Campaign processing context: Month ${calculationCurrentMonth} (${currentYear}), isSnapshot: ${isSnapshot}, contextDate: ${contextDate.toISOString().split('T')[0]}`);
    
    console.log(`🔍 Processing ${data.length} brands for campaign data`);
    data.forEach(brand => {
      if (!brand.campaigns || !Array.isArray(brand.campaigns)) {
        console.log(`⚠️ Brand ${brand.brand_name} has no campaigns`);
        return;
      }
      console.log(`📋 Processing brand ${brand.brand_name} with ${brand.campaigns.length} campaigns`);

      brand.campaigns.forEach(campaign => {
        // Debug campaign data
        console.log(`🎯 Campaign: ${campaign.campaign_name}`, {
          mediaTypesCount: campaign.media_types?.length || 0,
          firstMediaType: campaign.media_types?.[0],
          hasMonthlyBreakdown: campaign.media_types?.[0]?.monthly_breakdown ? 'YES' : 'NO',
          monthlyBreakdownLength: campaign.media_types?.[0]?.monthly_breakdown?.length || 0,
          monthlyBreakdownSample: campaign.media_types?.[0]?.monthly_breakdown?.[0],
          hasMonthlyData: campaign.media_types?.[0]?.monthly_data ? 'YES' : 'NO',
          monthlyDataLength: campaign.media_types?.[0]?.monthly_data?.length || 0,
          monthlyDataSample: campaign.media_types?.[0]?.monthly_data?.[0]
        });
        
        // Parse campaign data
        const campaignStart = new Date(campaign.start_date);
        const campaignEnd = new Date(campaign.end_date);
        const planningCost = parseFloat(campaign.planning_cost) || 0;
        const actualCost = parseFloat(campaign.actual_cost) || 0;

        // Determine status based on context month position
        // Handle year rollover for snapshot n-2 logic
        let calculationYear = currentYear;
        if (isSnapshot && calculationCurrentMonth <= 0) {
          calculationYear = currentYear - 1;
          const adjustedMonth = calculationCurrentMonth + 12; // Convert negative to previous year months
          var currentMonthDate = new Date(calculationYear, adjustedMonth - 1, 15); // Mid of calculation month
        } else {
          var currentMonthDate = new Date(calculationYear, calculationCurrentMonth - 1, 15); // Mid of context month
        }
        let autoStatus = '';
        
        if (currentMonthDate > campaignEnd) {
          autoStatus = 'done'; // Context month đã qua end date
        } else if (currentMonthDate >= campaignStart && currentMonthDate <= campaignEnd) {
          autoStatus = 'on-going'; // Context month trong khoảng start-end
        } else if (currentMonthDate < campaignStart) {
          autoStatus = 'planning'; // Context month chưa đến start date
        }

        // Use auto-detected status or fallback to original
        const status = autoStatus || campaign.campaign_status?.toLowerCase() || 'planning';

        // Calculate YTD/YTG from monthly data using n-2 logic
        let ytdSpending = 0; // YTD = Actual spending up to month n-2 (April for June snapshot)
        let ytgSpending = 0; // YTG = Remaining planning from month n-1 onwards
        let totalPlanning = 0; // Total planning across all months
        
        // First pass: aggregate monthly data
        const mediaTypesData = campaign.media_types || [];
        const monthlyDataMap = {};
        
        mediaTypesData.forEach(mediaType => {
          if (mediaType.monthly_breakdown && Array.isArray(mediaType.monthly_breakdown)) {
            mediaType.monthly_breakdown.forEach(monthData => {
              const monthKey = monthData.month; // Format: "2025-05"
              if (!monthlyDataMap[monthKey]) {
                monthlyDataMap[monthKey] = {
                  planning_cost: 0,
                  actual_cost: 0,
                  given_cost: 0
                };
              }
              
              monthlyDataMap[monthKey].planning_cost += parseFloat(monthData.planning_cost) || 0;
              monthlyDataMap[monthKey].actual_cost += parseFloat(monthData.actual_cost) || 0;
              monthlyDataMap[monthKey].given_cost += parseFloat(monthData.given_cost) || 0;
            });
          }
        });
        
        console.log(`📊 Monthly data map for ${campaign.campaign_name}:`, {
          monthlyDataMapKeys: Object.keys(monthlyDataMap),
          monthlyDataMap: monthlyDataMap,
          mediaTypesCount: mediaTypesData.length
        });
        
        // Second pass: calculate YTD (up to calculation month) and YTG (from next onwards)
        Object.keys(monthlyDataMap).forEach(monthKey => {
          const [year, month] = monthKey.split('-').map(Number);
          const monthData = monthlyDataMap[monthKey];
          
          totalPlanning += monthData.planning_cost;
          
          if (year === currentYear) {
            if (month <= calculationCurrentMonth) {
              // YTD months
              ytdSpending += monthData.actual_cost;
            } else {
              // YTG months (from next onwards)
              ytgSpending += monthData.planning_cost;
            }
          }
        });
        // Savings-to-date: only count months that have actual
        // planningToDate = sum(planning for months <= calc month where actual > 0)
        let planningToDate = 0;
        Object.keys(monthlyDataMap).forEach(monthKey => {
          const [year, month] = monthKey.split('-').map(Number);
          const monthData = monthlyDataMap[monthKey];
          if (year === currentYear && month <= calculationCurrentMonth) {
            if ((monthData.actual_cost || 0) > 0) {
              planningToDate += monthData.planning_cost || 0;
            }
          }
        });
        // Use backend savings data instead of calculating our own
        // This ensures consistency with CurrentDashboard and other tabs
        const backendSavings = enhancedMetrics?.financial_metrics?.total_savings || 0;
        const calculatedSavings = ytdSpending > 0 ? (planningToDate - ytdSpending) : 0;
        
        // For individual campaigns, use calculated savings
        // For total summary, we'll use backend total
        const savings = calculatedSavings;

        // Create monthly timeline using already processed monthly data
        const monthlyTimeline = {};
        
        // Map monthly data to timeline for all 12 months
        timelineMonths.forEach(month => {
          // Handle all monthly columns (January to December)
          const monthKey = `${month.year}-${month.month.toString().padStart(2, '0')}`;
          const monthData = monthlyDataMap[monthKey];
          
          if (monthData) {
            // Use API data for months with data
            const isCurrentOrPastMonth = month.month <= calculationCurrentMonth && month.year === currentYear;
            
            // Calculate YTD/YTG for this specific month
            const monthYTD = isCurrentOrPastMonth ? monthData.actual_cost : 0;
            const monthYTG = isCurrentOrPastMonth ? 0 : monthData.planning_cost;
            
            monthlyTimeline[`${month.year}-${month.month}`] = {
              isActive: true,
              monthlyPlanning: monthData.planning_cost,
              monthlyActual: monthData.actual_cost,
              ytd: monthYTD, // YTD only for months <= n-2
              ytg: monthYTG, // YTG for future months
              planning: monthData.planning_cost,
              isCurrentOrPastMonth: isCurrentOrPastMonth,
              hasApiData: true,
              isFutureMonth: !isCurrentOrPastMonth
            };
          } else {
            // No data for this month - show empty state or cumulative YTD for future months
            const isCurrentOrPastMonth = month.month <= calculationCurrentMonth && month.year === currentYear;
            const isFutureMonth = month.month > calculationCurrentMonth && month.year === currentYear;
            
            // For months without data in monthlyDataMap, show empty state
            let monthlyPlanningForCampaign = 0;
            
            // Debug logging for future months
            if (isFutureMonth && month.month <= 12) {
              console.log(`🔍 Future month ${month.month} (${month.name}) for campaign ${campaign.campaign_name}:`, {
                monthlyPlanningForCampaign,
                monthKey: `${month.year}-${month.month.toString().padStart(2, '0')}`,
                campaignStartDate: campaign.start_date,
                campaignEndDate: campaign.end_date,
                availableMonthlyDataKeys: Object.keys(monthlyDataMap),
                monthDataExists: monthlyDataMap[`${month.year}-${month.month.toString().padStart(2, '0')}`] ? 'YES' : 'NO'
              });
            }
            
            monthlyTimeline[`${month.year}-${month.month}`] = {
              isActive: isFutureMonth && monthlyPlanningForCampaign > 0, // Show as active if there's planning data for this campaign
              monthlyPlanning: 0,
              monthlyActual: 0,
              ytd: 0, // No YTD for future months
              ytg: monthlyPlanningForCampaign, // Use campaign-specific planning for this month
              planning: monthlyPlanningForCampaign, // Use campaign-specific planning for visualization
              isCurrentOrPastMonth: isCurrentOrPastMonth,
              isFutureMonth: isFutureMonth,
              hasApiData: false,
              isFutureMonth: isFutureMonth,
              isCumulativeYTG: isFutureMonth && monthlyPlanningForCampaign > 0 // Flag to indicate this campaign has planning data for this month
            };
          }
        });

        campaigns.push({
                campaign_id: campaign.campaign_id,
                campaign_name: campaign.campaign_name,
                brand_name: brand.brand_name,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          status: status,
          auto_status: autoStatus,
          planning_cost: totalPlanning, // Use calculated total planning
          actual_cost: actualCost, // Keep original for compatibility
          ytd_spending: ytdSpending, // YTD = Actual up to calculation month
          ytg_spending: ytgSpending, // YTG = Planning from next months onwards
          savings: savings, // Savings-to-date (planning to date - actual to date)
          progress_percentage: totalPlanning > 0 ? (ytdSpending / totalPlanning) * 100 : 0,
          monthly_timeline: monthlyTimeline,
          duration_days: Math.max(1, (campaignEnd - campaignStart) / (1000 * 60 * 60 * 24) + 1),
          calculation_current_month: calculationCurrentMonth,
          previous_month: previousMonth,
          monthly_data_available: Object.keys(monthlyDataMap).length > 0
        });
      });
    });

    return campaigns.sort((a, b) => a.brand_name.localeCompare(b.brand_name));
  }, [data, timelineMonths]);

  // Get unique brands for filter tabs
  const availableBrands = useMemo(() => {
    const brands = campaignData.map(campaign => campaign.brand_name).filter(Boolean);
    const uniqueBrands = ['All', ...Array.from(new Set(brands)).sort()];
    return uniqueBrands;
  }, [campaignData]);

  // Filter campaigns by selected brand
  const filteredCampaignData = useMemo(() => {
    if (selectedBrand === 'All') {
      return campaignData;
    }
    return campaignData.filter(campaign => campaign.brand_name === selectedBrand);
  }, [campaignData, selectedBrand]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value || value === 0) return '0';
    
    const num = Number(value);
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  // Get status color and chip
  const getStatusChip = (status, autoStatus) => {
    const statusConfig = {
      'done': { color: '#4CAF50', label: 'Done', bgcolor: alpha('#4CAF50', 0.1) },
      'on-going': { color: '#FF9800', label: 'On-going', bgcolor: alpha('#FF9800', 0.1) },
      'planning': { color: '#2196F3', label: 'Planning', bgcolor: alpha('#2196F3', 0.1) },
      'active': { color: '#FF9800', label: 'Active', bgcolor: alpha('#FF9800', 0.1) },
      'paused': { color: '#9E9E9E', label: 'Paused', bgcolor: alpha('#9E9E9E', 0.1) }
    };
    
    const config = statusConfig[status] || { color: '#666', label: status, bgcolor: alpha('#666', 0.1) };
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
        <Chip
          label={config.label}
          size="small"
          sx={{
            bgcolor: config.bgcolor,
            color: config.color,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            '& .MuiChip-label': {
              px: 1
            }
          }}
        />
        {autoStatus && (
          <Typography variant="caption" sx={{ 
            fontSize: { xs: '0.6rem', sm: '0.65rem' }, 
            color: '#666', 
            fontStyle: 'italic',
            lineHeight: 1.3
          }}>
            Auto
          </Typography>
        )}
      </Box>
    );
  };

  // Render timeline bar for a month
  const renderTimelineBar = (monthlyData, month) => {
    if (!monthlyData || !monthlyData.isActive) {
      // Show empty state for months without data
      const isFutureMonth = monthlyData?.isFutureMonth || false;
      
      return (
        <TableCell 
          align="center" 
          sx={{ 
            bgcolor: isFutureMonth ? alpha('#2196F3', 0.02) : '#fafafa',
            border: '1px solid #e0e0e0',
            width: '50px',
            minWidth: '50px',
            p: 0.25
          }}
        >
          <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ 
              color: isFutureMonth ? '#FF9800' : '#ccc', 
              fontSize: '0.7rem' 
            }}>
              {isFutureMonth ? '-' : '-'}
            </Typography>
          </Box>
        </TableCell>
      );
    }

    const ytd = monthlyData.ytd || 0; // YTD only for months <= n-1
    const ytg = monthlyData.ytg || 0; // Remaining budget for this month
    const planning = monthlyData.monthlyPlanning || 0;
    const isCurrentOrPastMonth = monthlyData.isCurrentOrPastMonth || false;
    const isFutureMonth = monthlyData.isFutureMonth || false;
    
    // Calculate percentages for visualization
    const total = planning;
    const ytdPercentage = total > 0 ? (ytd / total) * 100 : 0;
    const ytgPercentage = total > 0 ? (ytg / total) * 100 : 0;

    // Get current month info for comparison - Business Rule: n-2 logic
    const today = new Date();
    const actualCurrentMonth = today.getMonth() + 1; // 1-based
    const calculationCurrentMonth = actualCurrentMonth - 2; // Business Rule: n-2
    const previousMonth = calculationCurrentMonth - 1; // n-3

    return (
      <TableCell 
        align="center" 
        sx={{ 
          bgcolor: month.isCurrentMonth ? alpha('#2196F3', 0.03) : 
                   isFutureMonth ? alpha('#2196F3', 0.02) : 'white',
          border: month.isCurrentMonth ? `2px solid ${alpha('#2196F3', 0.3)}` : 
                  '1px solid #e0e0e0',
          width: '50px',
          minWidth: '50px',
          p: 0.25
        }}
      >
        <Tooltip 
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {month.fullName}
              </Typography>
              
              {isFutureMonth ? (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#FF9800' }}>
                    📊 YTG (Remaining Budget): {formatCurrency(ytg)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                    {ytg > 0 ? 'Total remaining budget from all campaigns for this month' : 'No remaining budget for this month'}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    💰 Monthly Planning: {formatCurrency(planning)}
                  </Typography>
                  
                  {isCurrentOrPastMonth ? (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#4CAF50' }}>
                        ✅ YTD (Actual Spending): {formatCurrency(ytd)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#FF9800' }}>
                        ⏳ YTG (Planning - Actual): {formatCurrency(ytg)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#2196F3' }}>
                        📅 Future Month
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#FF9800' }}>
                        📋 Planned Budget: {formatCurrency(ytg)}
                      </Typography>
                    </>
                  )}
                  
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    📊 Usage: {ytdPercentage.toFixed(1)}%
                  </Typography>
                  
                  {month.month === calculationCurrentMonth && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#2196F3', fontWeight: 600, mt: 0.5 }}>
                      🔵 Current Month (T{calculationCurrentMonth})
                    </Typography>
                  )}
                </>
              )}
            </Box>
          }
          placement="top"
        >
          <Box sx={{ height: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
            {/* Gantt Chart Bar */}
            <Box 
              sx={{ 
                width: '100%', 
                height: 20, 
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                border: '1px solid #e0e0e0',
                bgcolor: '#f5f5f5',
                position: 'relative'
              }}
            >
              {isFutureMonth ? (
                // Future month - show planning as YTG bar (orange like regular YTG)
                planning > 0 && (
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: '100%', 
                      bgcolor: '#FF9800', // Orange color like YTG
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px'
                    }}
                  >
                    <Typography variant="caption" sx={{ 
                      color: 'white', 
                      fontWeight: 600, 
                      fontSize: '0.7rem',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      YTG
                    </Typography>
                  </Box>
                )
              ) : (
                <>
                  {/* YTD section (Actual) - Green bar */}
                  {isCurrentOrPastMonth && ytdPercentage > 0 && (
                    <Box 
                      sx={{ 
                        width: `${Math.min(ytdPercentage, 100)}%`, 
                        height: '100%', 
                        bgcolor: '#4CAF50',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: ytdPercentage > 15 ? 'auto' : '20px'
                      }}
                    >
                      {ytdPercentage > 15 && (
                        <Typography variant="caption" sx={{ 
                          color: 'white', 
                          fontWeight: 600, 
                          fontSize: '0.7rem',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          YTD
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* YTG section (Remaining/Future) - Orange/Blue bar */}
                  {ytgPercentage > 0 && (
                    <Box 
                      sx={{ 
                        width: `${Math.min(ytgPercentage, isCurrentOrPastMonth ? 100 - ytdPercentage : 100)}%`, 
                        height: '100%', 
                        bgcolor: isCurrentOrPastMonth ? '#FF9800' : '#2196F3',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: ytgPercentage > 15 ? 'auto' : '20px'
                      }}
                    >
                      {ytgPercentage > 15 && (
                        <Typography variant="caption" sx={{ 
                          color: 'white', 
                          fontWeight: 600, 
                          fontSize: '0.7rem',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          YTG
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
            
            {/* Amount text */}
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.65rem', 
                fontWeight: 600,
                color: isFutureMonth ? '#FF9800' : 
                       isCurrentOrPastMonth ? '#4CAF50' : '#2196F3',
                textAlign: 'center'
              }}
            >
              {isFutureMonth ? formatCurrency(ytg) : formatCurrency(planning)}
            </Typography>
          </Box>
        </Tooltip>
      </TableCell>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      p: { xs: 1, sm: 2 },
      boxSizing: 'border-box'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ p: 0.5, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Card elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
              📊 Campaign Budget Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              mb: 2, 
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              lineHeight: 1.4
            }}>
              Track campaign budget progress across all 12 months. YTD = Actual spending for past/current months.
            </Typography>
            
            {/* Brand Filter Tabs */}
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={selectedBrand}
                onChange={(_, newValue) => setSelectedBrand(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-flexContainer': {
                    gap: 1
                  },
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 1,
                    border: '1px solid transparent',
                    '&.Mui-selected': {
                      bgcolor: alpha('#1976d2', 0.1),
                      border: `1px solid ${alpha('#1976d2', 0.3)}`,
                      color: '#1976d2'
                    },
                    '&:hover': {
                      bgcolor: alpha('#1976d2', 0.05)
                    }
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                {availableBrands.map((brand) => (
                  <Tab
                    key={brand}
                    label={brand}
                    value={brand}
                  />
                ))}
              </Tabs>
            </Box>
            
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#4CAF50', borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>YTD (Actual)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#FF9800', borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>YTG (Remaining)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#2196F3', borderRadius: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Future/YTG Sum</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: alpha('#2196F3', 0.2), borderRadius: 1, border: '2px solid #2196F3' }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Current (T5)</Typography>
              </Box>
            </Box>
            
            {/* Summary Cards */}
            <Grid container spacing={1}>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: alpha('#2196F3', 0.05), border: `1px solid ${alpha('#2196F3', 0.2)}`, borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#2196F3', 
                      fontWeight: 700, 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      lineHeight: 1.2
                    }}>
                      {formatCurrency(filteredCampaignData.reduce((sum, campaign) => sum + campaign.planning_cost, 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedBrand === 'All' ? 'Total Planning' : `${selectedBrand} Planning`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: alpha('#4CAF50', 0.05), border: `1px solid ${alpha('#4CAF50', 0.2)}`, borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#4CAF50', 
                      fontWeight: 700, 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      lineHeight: 1.2
                    }}>
                      {formatCurrency(filteredCampaignData.reduce((sum, campaign) => sum + campaign.ytd_spending, 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedBrand === 'All' ? 'Total YTD' : `${selectedBrand} YTD`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: alpha('#FF9800', 0.05), border: `1px solid ${alpha('#FF9800', 0.2)}`, borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#FF9800', 
                      fontWeight: 700, 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      lineHeight: 1.2
                    }}>
                      {formatCurrency(filteredCampaignData.reduce((sum, campaign) => sum + campaign.ytg_spending, 0))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedBrand === 'All' ? 'Total YTG' : `${selectedBrand} YTG`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: alpha('#4CAF50', 0.05), border: `1px solid ${alpha('#4CAF50', 0.2)}`, borderRadius: 1 }}>
                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#4CAF50', 
                      fontWeight: 700, 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      lineHeight: 1.2
                    }}>
                      {formatCurrency(enhancedMetrics?.financial_metrics?.total_savings || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedBrand === 'All' ? 'Total Savings (Backend)' : `${selectedBrand} Savings`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Gantt Chart Table */}
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table sx={{ minWidth: 900, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ 
                      fontWeight: 700, 
                      width: '180px', 
                      position: 'sticky', 
                      left: 0, 
                      bgcolor: alpha(theme.palette.primary.main, 0.08), 
                      zIndex: 2,
                      borderRight: '2px solid #e0e0e0',
                      fontSize: '0.8rem'
                    }}>
                      Campaign
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '70px', fontSize: '0.8rem' }} align="center">Brand</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '60px', fontSize: '0.8rem' }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '70px', fontSize: '0.8rem' }} align="center">Budget</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '70px', fontSize: '0.8rem' }} align="center">YTD</TableCell>
                    {timelineMonths.map((month) => (
                      <TableCell 
                        key={`${month.year}-${month.month}`}
                        sx={{ 
                          fontWeight: 700, 
                          width: '50px',
                          minWidth: '50px',
                          bgcolor: month.isCurrentMonth ? alpha('#2196F3', 0.15) : 
                                   alpha(theme.palette.primary.main, 0.08),
                          textAlign: 'center',
                          border: month.isCurrentMonth ? `2px solid ${alpha('#2196F3', 0.5)}` : 
                                  '1px solid #e0e0e0',
                          fontSize: '0.7rem',
                          p: 0.5
                        }}
                      >
                        <Typography variant="caption" sx={{ 
                          fontWeight: 700, 
                          display: 'block', 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2
                        }}>
                          {month.name}
                        </Typography>
                        {month.isCurrentMonth && (
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                            color: '#2196F3', 
                            fontWeight: 600,
                            lineHeight: 1.2
                          }}>
                            Current
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 700, width: '70px', fontSize: '0.8rem' }} align="center">
                      Savings
                    </TableCell>
              </TableRow>
                </TableHead>
            <TableBody>
                  {filteredCampaignData.map((campaign, index) => (
                    <TableRow 
                      key={campaign.campaign_id}
                      sx={{ 
                        bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.action.hover, 0.02),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      <TableCell sx={{ 
                        position: 'sticky', 
                        left: 0, 
                        bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.action.hover, 0.02),
                        zIndex: 1,
                        borderRight: '2px solid #e0e0e0',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        p: 1
                      }}>
                        <Box>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600, 
                            mb: 0.5, 
                            lineHeight: 1.2, 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}>
                            {campaign.campaign_name}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: '#666', 
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                            display: 'block',
                            lineHeight: 1.3
                          }}>
                            {new Date(campaign.start_date).toLocaleDateString('vi-VN')} - {new Date(campaign.end_date).toLocaleDateString('vi-VN')}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 500, 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2
                        }}>
                          {campaign.brand_name}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        {getStatusChip(campaign.status, campaign.auto_status)}
                      </TableCell>
                      
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600, 
                          color: '#2196F3', 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2
                        }}>
                          {formatCurrency(campaign.planning_cost)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 600, 
                          color: '#2196F3', 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2
                        }}>
                          {formatCurrency(campaign.ytd_spending)}
                        </Typography>
                  </TableCell>
                      
                      {timelineMonths.map((month) => {
                        const key = `${month.year}-${month.month}`;
                        return renderTimelineBar(campaign.monthly_timeline[key], month);
                      })}
                      
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        {campaign.status === 'done' && campaign.savings !== 0 ? (
                          <Box>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600, 
                              color: campaign.savings >= 0 ? '#4CAF50' : '#f44336',
                              fontSize: '0.7rem'
                            }}>
                              {campaign.savings >= 0 ? '+' : ''}{formatCurrency(campaign.savings)}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                            color: '#666', 
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                            display: 'block',
                            lineHeight: 1.3
                          }}>
                              {campaign.savings >= 0 ? 'Saved' : 'Over budget'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#ccc' }}>-</Typography>
                        )}
                      </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
            </Box>
          </CardContent>
        </Card>
        </Box>
      </motion.div>
    </Box>
  );
};

export default BudgetTab;